import { motion } from "framer-motion";
import {
  DollarSign,
  Eye,
  AlignJustify,
  PercentCircle,
  MousePointerClick,
  Percent,
  Smartphone,
  PiIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { FaUserGroup } from "react-icons/fa6";
import propertyService from "../../Services/propertyService";

export default function AppAnalyticsFormModal({
  modalOpen,
  handleSubmit,
  editItem,
  closeModal,
  publishers = [],
  adunits = []
}) {
  const [selectedPublisher, setSelectedPublisher] = useState(editItem?.publisherId || "");
  const [selectedApp, setSelectedApp] = useState(editItem?.publisherId || "");
  const [appOptions, setWebOptions] = useState([]);

  useEffect(() => {
    const fetchWebsites = async () => {
      if (selectedPublisher) {
        try {
          const result = await propertyService.getAppsitesByPublisher(selectedPublisher);
          setWebOptions(result);
        } catch (error) {
          console.error("Failed to fetch websites", error);
          setWebOptions([]);
        }
      } else {
        setWebOptions([]);
      }
    };

    fetchWebsites();
  }, [selectedPublisher]);

  if (!modalOpen) return null;

  const handleAppChange = (e) => {
    debugger
    var app = appOptions.find(x => x.id == e.target.value);
    setSelectedApp(app.name);
  }

  const formFields = [
    {
      custom: (
        <div className="relative group">
          <FaUserGroup className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 pointer-events-none" />
          <select
            name="publisher"
            value={selectedPublisher}
            onChange={(e) => setSelectedPublisher(e.target.value)}
            required
            className="appearance-none pl-10 pr-10 py-3 border rounded-lg w-full bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="" disabled>Select Publisher</option>
            {publishers.map((pub) => (
              <option key={pub.id} value={pub.id}>{pub.name}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            ▼
          </div>
        </div>
      ),
    },
    {
      custom: (
        <div className="relative group">
          <Smartphone className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 pointer-events-none" />
          <select name="propertyId"
            defaultValue={editItem?.app || ""}
            onChange={handleAppChange}
            required
            className="appearance-none pl-10 pr-10 py-3 border rounded-lg w-full bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 group-hover:border-indigo-400 transition-all duration-300 ease-in-out"
          >
            <option value="" disabled>Select App</option>
            {appOptions.map((app) => (
              <option key={app.id} value={app.id}>
                {app.appId}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            ▼
          </div>
        </div>
      ),
    },
    // Adunit Dropdown
    {
      custom: (
        <div className="relative group">
          <PiIcon className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 pointer-events-none" />
          <select
            name="adunit"
            defaultValue={editItem?.adunit || ""}
            required
            className="appearance-none pl-10 pr-10 py-3 border rounded-lg w-full bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 group-hover:border-indigo-400 transition-all duration-300 ease-in-out"
          >
            <option value="" disabled>Select Adunit</option>
            {adunits.map((adunit) => (
              <option key={adunit.name} value={adunit.name}>
                {adunit.name}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            ▼
          </div>
        </div>
      ),
    },
    {
      icon: DollarSign,
      name: "revenue",
      placeholder: "Revenue",
      type: "number",
      step: "0.01"
    },
    {
      icon: Eye,
      name: "impressions",
      placeholder: "Ad Impressions",
      type: "number",
      step: "0.01"
    },
    {
      icon: AlignJustify,
      name: "adRequests",
      placeholder: "Ad Requests",
      type: "number",
      step: "0.01"
    },
    {
      icon: PercentCircle,
      name: "viewability",
      placeholder: "Viewability (%)",
      type: "number",
      step: "0.01"
    },
    {
      icon: MousePointerClick,
      name: "clicks",
      placeholder: "Clicks",
      type: "number"
    },
    {
      icon: DollarSign,
      name: "ecpm",
      placeholder: "eCPM",
      type: "number",
      step: "0.01"
    },
    {
      icon: Percent,
      name: "fillRate",
      placeholder: "Fill Rate (%)",
      type: "number",
      step: "0.01"
    },
    {
      icon: Percent,
      name: "ctr",
      placeholder: "CTR (%)",
      type: "number",
      step: "0.01"
    },
    {
      custom: (
        <div className="relative group">
          <input
            name="date"
            type="date"
            defaultValue={editItem?.date || ""}
            required
            className="pl-10 pr-4 py-3 border rounded-lg w-full text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500 group-hover:border-indigo-400 transition-all duration-300 ease-in-out"
          />
        </div>
      ),
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
      <div className="bg-white rounded-2xl p-6 w-full max-w-4xl shadow-xl overflow-y-auto max-h-[90vh]">
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <motion.h2
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-2 text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 animate-gradient-x drop-shadow-sm"
          >
            {editItem ? "Update App Analytics" : "Add App Analytics"}
          </motion.h2>

          {/* ✅ Separate selected app display */}
          {selectedApp ? (
            <div className="md:col-span-2 text-sm text-indigo-600 font-medium -mt-3">
              Selected App: <span className="font-semibold">{selectedApp}</span>
            </div>
          ) : (
            <div className="md:col-span-2 text-sm text-gray-500 -mt-3">
              No App selected
            </div>
          )}


          {formFields.map((field, index) =>
            field.custom ? (
              <div key={index}>{field.custom}</div>
            ) : (
              <div key={field.name} className="relative group">
                <field.icon className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500" />
                <input
                  name={field.name}
                  type={field.type}
                  placeholder={field.placeholder}
                  defaultValue={
                    editItem?.[field.name] !== undefined
                      ? Number(editItem[field.name]).toFixed(2)
                      : ""
                  }
                  className="pl-10 pr-4 py-2.5 border rounded-lg w-full focus:ring-2 focus:ring-indigo-500"
                  required
                  min={field.min}
                  max={field.max}
                  step={field.step}
                />
              </div>
            )
          )}

          <div className="md:col-span-2 flex gap-4 justify-end pt-4">

            <button
              type="submit"
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:brightness-110 transition shadow hover:shadow-lg"
            >
              {editItem ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={closeModal}
              className="bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
